import { resolveVideoPoster } from "@/components/ui/lightbox/utils";
import { isPdf } from "@/libs/file-helper";
import {
  classifyAttachmentKind,
  resolveAttachmentExtension,
} from "./attachment-kind";

export type AttachmentFile = {
  fileName: string;
  originalContentUri?: string | null;
  thumbnailUri?: string | null;
  contentType?: string | null;
};

/** How a tile paints its file. */
export type AttachmentPreviewKind =
  /** An image, or a video's poster, filling the tile. */
  | { kind: "picture"; src: string; isVideo: boolean }
  /** A document with a rendered first page, laid on a page card. */
  | { kind: "page"; src: string }
  /** A PDF with no rendition — its first page is rendered in the browser from `src`. */
  | { kind: "pdf"; src: string }
  /** Nothing to paint — the type icon on a blank page card. */
  | { kind: "icon" };

/**
 * Pure. Which picture a tile shows. Audio never has one; a document has one when the
 * pipeline rendered its first page into `thumbnailUri`, and a PDF without one has its
 * first page rendered from the file itself.
 */
export function resolveAttachmentPreviewKind(
  file: AttachmentFile,
): AttachmentPreviewKind {
  const extension = resolveAttachmentExtension(file);
  const kind = classifyAttachmentKind(extension, file.contentType);

  if (kind === "video") {
    const poster = resolveVideoPoster(file);
    return poster
      ? { kind: "picture", src: poster, isVideo: true }
      : { kind: "icon" };
  }

  if (kind === "image") {
    const src = file.thumbnailUri || file.originalContentUri;
    return src ? { kind: "picture", src, isVideo: false } : { kind: "icon" };
  }

  if (kind !== "other") return { kind: "icon" };

  // Some callers fall the thumbnail back to the file itself — a PDF is not a rendition.
  if (file.thumbnailUri && file.thumbnailUri !== file.originalContentUri) {
    return { kind: "page", src: file.thumbnailUri };
  }

  const isPdfFile =
    isPdf(extension) || file.contentType?.toLowerCase() === "application/pdf";
  if (isPdfFile && file.originalContentUri) {
    return { kind: "pdf", src: file.originalContentUri };
  }

  return { kind: "icon" };
}
